import { useMemo } from "react";
import { durationSeconds } from "@if-tracking/shared";
import type { Fast } from "@if-tracking/shared";

import { formatDate, formatDuration, formatTime } from "../format";

interface FastListProps {
  fasts: Fast[];
  timezone: string;
  grouped?: boolean;
  emptyHint?: string;
  onEdit: (fast: Fast) => void;
  onDelete: (fast: Fast) => void;
}

function yearOf(epochMs: number, timeZone: string): string {
  return new Intl.DateTimeFormat("en-US", { timeZone, year: "numeric" }).format(new Date(epochMs));
}

export function FastList({ fasts, timezone, grouped = false, emptyHint, onEdit, onDelete }: FastListProps) {
  const rows = useMemo(() => {
    const out: ({ kind: "band"; year: string; count: number } | { kind: "row"; fast: Fast })[] = [];
    let currentYear = "";
    let yearCount = 0;
    let bandIndex = -1;
    for (const fast of fasts) {
      if (grouped) {
        const year = yearOf(fast.start_ts, timezone);
        if (year !== currentYear) {
          currentYear = year;
          out.push({ kind: "band", year, count: 0 });
          bandIndex = out.length - 1;
          yearCount = 0;
        }
        yearCount += 1;
        const band = out[bandIndex];
        if (band && band.kind === "band") band.count = yearCount;
      }
      out.push({ kind: "row", fast });
    }
    return out;
  }, [fasts, grouped, timezone]);

  if (fasts.length === 0) {
    return <div className="empty">{emptyHint ?? "No fasts in this range."}</div>;
  }

  return (
    <div className="history-list">
      {rows.map((item) =>
        item.kind === "band" ? (
          <div className="year-band" key={`band-${item.year}`}>
            {item.year} · {item.count}
          </div>
        ) : (
          <FastRow key={item.fast.id} fast={item.fast} timezone={timezone} onEdit={onEdit} onDelete={onDelete} />
        )
      )}
    </div>
  );
}

interface FastRowProps {
  fast: Fast;
  timezone: string;
  onEdit: (fast: Fast) => void;
  onDelete: (fast: Fast) => void;
}

function FastRow({ fast, timezone, onEdit, onDelete }: FastRowProps) {
  const active = fast.end_ts === null;
  const seconds = active ? 0 : durationSeconds(fast.start_ts, fast.end_ts as number);
  const missed = !active && seconds < fast.target_seconds;

  return (
    <div className="fast-row">
      <div className="lead">
        <span className="start">
          {formatDate(fast.start_ts, timezone)}, {formatTime(fast.start_ts, timezone)}
        </span>
        {active ? (
          <span className="sub active">Active</span>
        ) : (
          <span className="sub">ends {formatTime(fast.end_ts as number, timezone)}</span>
        )}
      </div>
      <div className="trail">
        <span className={missed ? "dur num missed" : "dur num"}>
          {active ? "—" : formatDuration(seconds)}
        </span>
      </div>
      <div className="actions">
        <button className="link-button" onClick={() => onEdit(fast)}>
          Edit
        </button>
        <button className="link-button danger" onClick={() => onDelete(fast)}>
          Delete
        </button>
      </div>
    </div>
  );
}
